import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { getRequest, postRequest } from "../../services/apiService";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableDeleteDialog from "../../common/ReusableDeleteDialog";
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import ReusableForm from "../../common/ReusableForm"; // Import ReusableForm

const API_URL = "/access/privileges/";

const Privileges = () => {
  const [privileges, setPrivileges] = useState([]);
  const [showPrivilegeModal, setShowPrivilegeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const toast = useRef(null);
  const menuRef = useRef(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });

  const [privilegeForm, setPrivilegeForm] = useState({
    name: "",
    description: "",
    status_db: true // Changed to boolean for checkbox
  });

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const privilegeFormFields = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      placeholder: "Enter privilege name"
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      placeholder: "Describe the privilege's purpose and scope"
    },
    {
      name: "status_db",
      label: "Active",
      type: "checkbox",
      helpText: "Toggle to activate or deactivate the privilege."
    }
  ];

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const fetchPrivileges = async () => {
    setLoading(true);
    try {
      const response = await getRequest(API_URL);
      if (response) {
        // Assuming response is an array of privilege objects
        setPrivileges(response);
        // If the API provides total records, update it. Otherwise, use length of fetched data.
        setTotalRecords(response.length); 
      }
    } catch (error) {
      showToast("error", "Error", "Failed to fetch privileges");
      console.error("Error fetching privileges:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrivileges();
  }, []);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const openAddModal = () => {
    setIsEditing(false);
    setPrivilegeForm({
      name: "",
      description: "",
      status_db: true // Default to active
    });
    setShowPrivilegeModal(true);
  };

  const openEditModal = (privilege) => {
    setIsEditing(true);
    setPrivilegeForm({
      id: privilege.id,
      name: privilege.name,
      description: privilege.description || "",
      status_db: privilege.status_db === 1 // Convert to boolean for checkbox
    });
    setSelectedPrivilege(privilege);
    setShowPrivilegeModal(true);
  };

  const confirmDeletePrivilege = (privilege) => {
    setSelectedPrivilege(privilege);
    setShowDeleteModal(true);
    setDeleteFormData({ confirmation: "" });
  };

  const deletePrivilege = async (e) => {
    e.preventDefault();

    if (deleteFormData.confirmation !== "DELETE") {
      showToast("error", "Error", "Please type 'DELETE' to confirm");
      return;
    }

    try {
      await postRequest(`${API_URL}`, {
        id: selectedPrivilege.id,
        status_db: 3
      });
      showToast("success", "Success", "Privilege deleted successfully");
      fetchPrivileges();
      setShowDeleteModal(false);
      setSelectedPrivilege(null);
      setDeleteFormData({ confirmation: "" });
    } catch (error) {
      showToast("error", "Error", "Failed to delete privilege");
      console.error("Error deleting privilege:", error);
    }
  };

  const savePrivilege = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (!privilegeForm.name) {
      showToast("error", "Error", "Name is required");
      return;
    }

    try {
      const payload = {
        name: privilegeForm.name,
        description: privilegeForm.description,
        status_db: privilegeForm.status_db ? 1 : 0 // Convert boolean back to 1 or 0
      };

      if (isEditing) {
        payload.id = privilegeForm.id;
        await postRequest(`${API_URL}`, payload);
        showToast("success", "Success", "Privilege updated successfully");
      } else {
        await postRequest(API_URL, payload);
        showToast("success", "Success", "Privilege added successfully");
      }
      fetchPrivileges();
      setShowPrivilegeModal(false);
    } catch (error) {
      showToast("error", "Error", "Failed to save privilege");
      console.error("Error saving privilege:", error);
    }
  };

  const nameBodyTemplate = (rowData) => (
    <span className="font-medium">{rowData.name}</span>
  );

  const descriptionBodyTemplate = (rowData) => (
    <span className="text-sm text-gray-600 line-clamp-2">{rowData.description || "—"}</span>
  );

  const actionBodyTemplate = (rowData) => {
    const items = [
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => openEditModal(rowData),
      },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () => confirmDeletePrivilege(rowData),
      },
    ];

    return (
      <div className="flex justify-start">
        <Button
          icon="pi pi-ellipsis-v"
          className="p-button-rounded p-button-text"
          onClick={(event) => {
            setSelectedPrivilege(rowData);
            menuRef.current.toggle(event);
          }}
          aria-controls="privilege_menu"
          aria-haspopup
        />
        <Menu model={items} popup ref={menuRef} id="privilege_menu" />
      </div>
    );
  };

  const columns = [
    { field: "name", header: "Name", sortable: true, body: nameBodyTemplate, className: "w-1/3" },
    { field: "description", header: "Description", sortable: true, body: descriptionBodyTemplate, className: "w-1/2" },
    { header: "Actions", body: actionBodyTemplate, className: "w-1/6 text-center" }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 max-w-full">
      <Toast ref={toast} position="top-right" />

      <ReusableTable
        title="Privileges"
        data={privileges}
        columnFields={columns}
        loading={loading}
        totalRecords={totalRecords}
        rows={rowsPerPage}
        onPageChange={onPageChange}
        first={(currentPage - 1) * rowsPerPage}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        setAddNewDialogVisible={openAddModal}
        paginator
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        rowsPerPageOptions={[5, 10, 25, 50]}
        className="p-datatable-sm"
        colorScheme={{
          headerBgColor: '#1e40af',
          headerTextColor: '#ffffff',
          tableBgColor: '#f3f4f6',
          tableTextColor: '#1f2937',
        }}
        emptyMessage={
          <div className="flex flex-col items-center justify-center py-12">
            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-500 text-lg">No privileges found</p>
            <button
              onClick={openAddModal}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Your First Privilege
            </button>
          </div>
        }
      />

      {showPrivilegeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="w-1/2">
          <ReusableForm
            formData={privilegeForm}
            setFormData={setPrivilegeForm}
            fields={privilegeFormFields}
            onSubmit={savePrivilege}
            onHide={() => setShowPrivilegeModal(false)}
            headerText={isEditing ? "Edit Privilege" : "Add New Privilege"}
            submitLabel={isEditing ? "Update" : "Save"}
          />
          </div>
        </div>
      )}

      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteFormData({ confirmation: "" });
        }}
        onDelete={deletePrivilege}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Privilege"
        description={selectedPrivilege && `You are about to delete the privilege "${selectedPrivilege.name}". This action cannot be undone.`}
        deleteLabel="Delete Privilege"
      />
    </div>
  );
};

export default Privileges;